import type { TFunction } from 'i18next'
import type { Component } from 'vue'

import {
  Bars3BottomLeftIcon,
  BookOpenIcon,
  ListBulletIcon,
  Squares2X2Icon,
} from '@heroicons/vue/24/outline'

import type { BRMOrganizationBase } from '../types/external/2work/doctypes/CustomDoctypes.ts'
import type { Contact } from '../types/external/2work/doctypes/generatorOutput/Contacts/Contact.ts'
import type { EmployeeDynamic } from '../types/external/2work/doctypes/generatorOutput/Setup/EmployeeDynamic.ts'

interface RelationshipsCardBaseProps {
  cardType: RelationGroupType
  subTitle: string
  title: string
  uid: string
  isFavorite?: boolean
  rating?: string | undefined
  responsibleEmployee?: Pick<EmployeeDynamic, 'employee_name' | 'image' | 'name'> | undefined
  userTags?: string[] | undefined
}

interface ExtendedCardProps {
  last_interaction: string
  docname?: string
  doctype?: string
}

interface PersonCardProps extends RelationshipsCardBaseProps {
  cardType: RelationGroupType.persons
  furtherOrganizations?: BRMOrganizationBase[] | undefined
}

interface OrganizationCardProps extends RelationshipsCardBaseProps {
  cardType: RelationGroupType.organizations
  currentContacts?: Contact[] | null | undefined
  image?: string | undefined
  isPersonCustomer?: boolean | undefined
  pipelineState?: string | undefined
  relationType?: string | undefined
}

type ExtendedPersonCardProps = ExtendedCardProps & PersonCardProps
type ExtendedOrganizationCardProps = ExtendedCardProps & OrganizationCardProps

type ExtendedRelationshipsCardProps = ExtendedOrganizationCardProps | ExtendedPersonCardProps

type RelationTiles = Record<InteractionIntervals, ExtendedRelationshipsCardProps[]>

enum RelationType {
  all = 'All',
  starred = 'Favorites',
  customers = 'Customer',
  suppliers = 'Supplier',
  partners = 'TW Partner',
  salesPartners = 'Sales Partner',
  investors = 'Bank',
  other = 'TW Other',
}

const organizationDoctypes = [
  'customer',
  'supplier',
  'sales-partner',
  'tw-other',
  'tw-partner',
  'bank',
]

enum RelationGroupType {
  organizations = 'Organizations',
  persons = 'Persons',
}
enum PersonGroupType {
  employee = 'Employee',
  user = 'User',
}

enum DataViewType {
  cards = 'cards',
  list = 'list',
  kanban = 'kanban',
  report = 'report',
  tree = 'tree',
  inbox = 'inbox',
  outbox = 'outbox',
}

enum InteractionIntervals {
  TODAY = 'today',
  LAST_WEEK = 'lastWeek',
  LAST_MONTH = 'lastMonth',
  LAST_YEAR = 'lastYear',
  LATER = 'later',
}

const breadCrumbs = [
  { text: 'Home', url: '/' },
  { text: 'Beziehungen', url: '/relationships' },
]

interface DataViewTab {
  icon: Component
  key: DataViewType
  name: string
  disabled?: boolean
}

const dataViewTabs = (tFunc: TFunction): DataViewTab[] => {
  return [
    {
      disabled: true,
      icon: Bars3BottomLeftIcon,
      key: DataViewType.kanban,
      name: tFunc('relationTab.kanban'),
    },
    { icon: Squares2X2Icon, key: DataViewType.cards, name: tFunc('relationTab.cards') },
    { icon: ListBulletIcon, key: DataViewType.list, name: tFunc('relationTab.list') },
    {
      disabled: true,
      icon: BookOpenIcon,
      key: DataViewType.report,
      name: tFunc('relationTab.report'),
    },
  ]
}

export {
  breadCrumbs,
  type DataViewTab,
  dataViewTabs,
  DataViewType,
  type ExtendedOrganizationCardProps,
  type ExtendedPersonCardProps,
  type ExtendedRelationshipsCardProps,
  InteractionIntervals,
  type OrganizationCardProps,
  organizationDoctypes,
  type PersonCardProps,
  PersonGroupType,
  RelationGroupType,
  type RelationTiles,
  RelationType,
}
